/**
 * LHD API client — typed thin wrappers over the shared credentialed http core.
 * Every call maps 1:1 to a real route in `apps/api/src/modules/lhd/controllers/**`
 * so the front-end call-table diffs clean against the route table (no-dead-UI gate).
 *
 * Mutations that edit an entity always send `version` for the optimistic lock;
 * the http core maps a 409 to a typed conflict the screens surface as the
 * "already resolved / version changed" toast.
 */
import { http } from '@teivor/shared-utils/api';

import type {
  Agent,
  AgentListQuery,
  AttentionCard,
  AttentionListQuery,
  Conversation,
  ConversationDetail,
  ConversationListQuery,
  ConversationMessage,
  CreateAgentInput,
  CreateConversationFromTriageResult,
  EstimateTestRunResult,
  InboxLite,
  MatchTriageInput,
  Paginated,
  RepUser,
  TestHarnessExample,
  TestHarnessRun,
  TriageItem,
  TriageListQuery,
  UpdateAgentInput,
  AgentAction,
  Script,
  ScriptTree,
  ScriptStage,
  ScriptBranch,
  ScriptListQuery,
  CreateScriptInput,
  UpdateScriptInput,
  CreateStageInput,
  UpdateStageInput,
  CreateBranchInput,
  UpdateBranchInput,
  AgentDocument,
  AgentDocumentListQuery,
  CreateAgentDocumentMeta,
  UpdateAgentDocumentInput,
  AIDecision,
  InboxFacets,
  ReplyTurnStatus,
  Booking,
  BookingListQuery,
  LogBookingOutcomeInput,
} from './types';

const clean = <T extends Record<string, unknown>>(q: T): T => q;

export const lhdApi = {
  // ----- Workspace Inbox + Conversation thread --------------------------------
  listConversations: (q: ConversationListQuery) =>
    http.get<Paginated<Conversation>>('/api/lhd/inbox', clean({
      page: q.page,
      pageSize: q.pageSize,
      campaignId: q.campaignId,
      agentId: q.agentId,
      state: q.state,
      needsAttention: q.needsAttention,
      kind: q.kind,
    })),
  getConversation: (id: string) =>
    http.get<ConversationDetail>(`/api/lhd/inbox/${id}`),
  /** Distinct campaigns/agents used by this workspace's conversations (filter facets). */
  inboxFacets: () => http.get<InboxFacets>('/api/lhd/inbox/facets'),
  listMessages: (id: string, page?: number, pageSize?: number) =>
    http.get<Paginated<ConversationMessage>>(`/api/lhd/inbox/${id}/messages`, clean({ page, pageSize })),
  composeMessage: (id: string, body: string) =>
    http.post<ConversationMessage>(`/api/lhd/inbox/${id}/messages`, { body }),
  /** English-only mechanical copy-edit (typos / punctuation / capitalization) of a draft being edited. */
  grammarCheck: (text: string) =>
    http.post<{ text: string; changed: boolean }>('/api/lhd/inbox/grammar-check', { text }),
  uploadAttachment: (id: string, fileId: string, filename: string) =>
    http.post<ConversationMessage>(`/api/lhd/inbox/${id}/attachments`, { fileId, filename }),
  /** Live per-turn processing status (read → decide → write → gates / retry / error); null when none. */
  turnStatus: (id: string) =>
    http.get<ReplyTurnStatus | null>(`/api/lhd/inbox/${id}/turn-status`),

  // ----- Conversation lifecycle / human control -------------------------------
  takeOver: (id: string, version: number) =>
    http.post<Conversation>(`/api/lhd/conversations/${id}/take-over`, { version }),
  handBack: (id: string, version: number) =>
    http.post<Conversation>(`/api/lhd/conversations/${id}/hand-back`, { version }),
  handoff: (id: string, version: number) =>
    http.post<Conversation>(`/api/lhd/conversations/${id}/handoff`, { version }),
  takeOverFromRep: (id: string, version: number) =>
    http.post<Conversation>(`/api/lhd/conversations/${id}/take-over-from-rep`, { version }),
  assignConversation: (id: string, version: number, amUserId: string) =>
    http.post<Conversation>(`/api/lhd/conversations/${id}/assign`, { version, amUserId }),
  bulkAssign: (conversationIds: string[], amUserId: string) =>
    http.post<{ assigned: number; skipped: string[] }>(
      '/api/lhd/conversations/bulk-assign',
      { conversationIds, amUserId },
    ),
  closeConversation: (id: string, version: number, reason: string) =>
    http.post<Conversation>(`/api/lhd/conversations/${id}/close`, { version, reason }),
  setDisposition: (id: string, version: number, disposition: string) =>
    http.post<Conversation>(`/api/lhd/conversations/${id}/disposition`, { version, disposition }),

  // ----- Admin "System actions" (#6) — re-run agent + raw AI decision view -----
  /** Re-run the agent on the conversation's latest inbound reply (regenerates the draft). */
  rerunAgent: (conversationId: string) =>
    http.post<{ ok: true }>(`/api/lhd/inbox/${conversationId}/rerun-agent`, {}),
  /**
   * Raw model-decision rows for a conversation (admin debugging). NOTE: this is an
   * AIG route (`/api/aig/decisions`), not under `/api/lhd`.
   */
  listConversationDecisions: (workspaceId: string, conversationRef: string) =>
    http.get<AIDecision[]>(
      '/api/aig/decisions',
      clean({ workspaceId, requestType: 'lhd.stage2.agent', conversationRef, limit: 20 }),
    ),

  // ----- Attention Queue ------------------------------------------------------
  listAttention: (q: AttentionListQuery) =>
    http.get<Paginated<AttentionCard>>('/api/lhd/attention', clean({
      page: q.page,
      pageSize: q.pageSize,
      status: q.status,
      cardType: q.cardType,
    })),
  /**
   * The Inbox nav badge. Deliberately NOT `listAttention(...).total` — the badge counts only drafts
   * awaiting approval, while the queue still lists everything. See the service for why.
   */
  attentionNotificationCount: () =>
    http.get<{ count: number }>('/api/lhd/attention/notification-count'),
  getAttentionCard: (cardId: string) =>
    http.get<AttentionCard>(`/api/lhd/attention/${cardId}`),
  approveCard: (cardId: string, version: number) =>
    http.post<AttentionCard>(`/api/lhd/attention/${cardId}/approve`, { version }),
  editApproveCard: (cardId: string, version: number, editedBody: string) =>
    http.post<AttentionCard>(`/api/lhd/attention/${cardId}/edit-approve`, { version, editedBody }),
  rejectCard: (cardId: string, version: number) =>
    http.post<AttentionCard>(`/api/lhd/attention/${cardId}/reject`, { version }),
  takeOverCard: (cardId: string, version: number) =>
    http.post<AttentionCard>(`/api/lhd/attention/${cardId}/take-over`, { version }),
  createDsarFromCard: (cardId: string, version: number) =>
    http.post<AttentionCard>(`/api/lhd/attention/${cardId}/create-dsar`, { version }),
  approveLanguageFromCard: (cardId: string, version: number, languageCode: string) =>
    http.post<AttentionCard>(`/api/lhd/attention/${cardId}/approve-language`, { version, languageCode }),
  attachAliasFromCard: (cardId: string, version: number, personId: string) =>
    http.post<AttentionCard>(`/api/lhd/attention/${cardId}/attach-alias`, { version, personId }),
  confirmHandoffFromCard: (cardId: string, version: number) =>
    http.post<AttentionCard>(`/api/lhd/attention/${cardId}/confirm-handoff`, { version }),
  /** Override the planner's proposed next stage on a pending action before approving. */
  overridePlannedStage: (cardId: string, version: number, plannedStageId: string) =>
    http.post<AgentAction>(`/api/lhd/attention/${cardId}/planned-stage`, { version, plannedStageId }),
  /** Attach (or clear, documentId=null) a document on a pending draft action (Phase 2 — human pick). */
  attachDocumentToCard: (cardId: string, version: number, documentId: string | null) =>
    http.post<AgentAction>(`/api/lhd/attention/${cardId}/attach-document`, { version, documentId }),
  /** Append a presigned download link for a clean document to the draft body (Phase 2 — link delivery). */
  insertDocumentLink: (cardId: string, version: number, documentId: string) =>
    http.post<AgentAction>(`/api/lhd/attention/${cardId}/insert-document-link`, { version, documentId }),

  // ----- Agents list + detail -------------------------------------------------
  listAgents: (q: AgentListQuery) =>
    http.get<Paginated<Agent>>('/api/lhd/agents', clean({
      page: q.page,
      pageSize: q.pageSize,
      status: q.status,
    })),
  getAgent: (id: string) => http.get<Agent>(`/api/lhd/agents/${id}`),
  createAgent: (body: CreateAgentInput) => http.post<Agent>('/api/lhd/agents', body),
  updateAgent: (id: string, body: UpdateAgentInput) => http.patch<Agent>(`/api/lhd/agents/${id}`, body),
  archiveAgent: (id: string, version: number) =>
    http.post<Agent>(`/api/lhd/agents/${id}/archive`, { version }),

  // ----- Scripts (conversation playbooks) -------------------------------------
  listScripts: (q: ScriptListQuery) =>
    http.get<Paginated<Script>>('/api/lhd/scripts', clean({
      page: q.page,
      pageSize: q.pageSize,
      status: q.status,
    })),
  getScript: (id: string) => http.get<ScriptTree>(`/api/lhd/scripts/${id}`),
  createScript: (body: CreateScriptInput) => http.post<Script>('/api/lhd/scripts', body),
  updateScript: (id: string, body: UpdateScriptInput) =>
    http.patch<Script>(`/api/lhd/scripts/${id}`, body),
  archiveScript: (id: string, version: number) =>
    http.post<Script>(`/api/lhd/scripts/${id}/archive`, { version }),
  createStage: (scriptId: string, body: CreateStageInput) =>
    http.post<ScriptStage>(`/api/lhd/scripts/${scriptId}/stages`, body),
  updateStage: (scriptId: string, stageId: string, body: UpdateStageInput) =>
    http.patch<ScriptStage>(`/api/lhd/scripts/${scriptId}/stages/${stageId}`, body),
  deleteStage: (scriptId: string, stageId: string, version: number) =>
    http.delete<void>(`/api/lhd/scripts/${scriptId}/stages/${stageId}`, { version }),
  createBranch: (scriptId: string, stageId: string, body: CreateBranchInput) =>
    http.post<ScriptBranch>(`/api/lhd/scripts/${scriptId}/stages/${stageId}/branches`, body),
  updateBranch: (scriptId: string, stageId: string, branchId: string, body: UpdateBranchInput) =>
    http.patch<ScriptBranch>(
      `/api/lhd/scripts/${scriptId}/stages/${stageId}/branches/${branchId}`,
      body,
    ),
  deleteBranch: (scriptId: string, stageId: string, branchId: string, version: number) =>
    http.delete<void>(`/api/lhd/scripts/${scriptId}/stages/${stageId}/branches/${branchId}`, {
      version,
    }),

  // ----- Agent documents (Phase 2 — outbound attachments) ---------------------
  listDocuments: (q: AgentDocumentListQuery) =>
    http.get<AgentDocument[]>('/api/lhd/documents', clean({ status: q.status, tag: q.tag })),
  getDocument: (id: string) => http.get<AgentDocument>(`/api/lhd/documents/${id}`),
  /** Multipart upload — the binary plus name/description/tags metadata. */
  createDocument: (meta: CreateAgentDocumentMeta, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', meta.name);
    if (meta.description) fd.append('description', meta.description);
    fd.append('tags', JSON.stringify(meta.tags ?? []));
    return http.post<AgentDocument>('/api/lhd/documents', fd);
  },
  updateDocument: (id: string, body: UpdateAgentDocumentInput) =>
    http.patch<AgentDocument>(`/api/lhd/documents/${id}`, body),
  archiveDocument: (id: string, version: number) =>
    http.post<AgentDocument>(`/api/lhd/documents/${id}/archive`, { version }),
  unarchiveDocument: (id: string, version: number) =>
    http.post<AgentDocument>(`/api/lhd/documents/${id}/unarchive`, { version }),
  deleteDocument: (id: string, version: number) =>
    http.delete<{ ok: true }>(`/api/lhd/documents/${id}`, { version }),

  // ----- Cross-module pickers for agent identity ------------------------------
  // The agent's sending inbox lives in OUT and its rep is an IAM client-portal
  // user; both feed the agent-detail pickers. (Routes: out/inboxes,
  // iam/workspaces/:id/client-users — not lhd/*.)
  /** Sending-inbox candidates for a workspace (GET /api/out/inboxes?workspaceId=…). */
  listInboxes: (workspaceId: string) =>
    http.get<InboxLite[]>('/api/out/inboxes', { workspaceId }),
  /** Rename an inbox's display name — the human "From" name (PATCH /api/out/inboxes/:id). */
  patchInboxDisplayName: (id: string, displayName: string | null, version: number) =>
    http.patch<InboxLite>(`/api/out/inboxes/${id}`, { displayName, version }),
  /**
   * Rep-user candidates — the workspace's client-portal sales reps
   * (GET /api/iam/workspaces/:id/client-users, filtered to role=client_salesman).
   * Only client-portal roles can log into the rep portal and see their rep tasks
   * (rep-task self-scoping keys on role==='client_salesman'), so a staff
   * 'salesman' picked here would never see the handoff — hence this endpoint,
   * which is also workspace-scoped (iam.users.workspace_id), not global.
   */
  listReps: (workspaceId: string) =>
    http
      .get<RepUser[]>(`/api/iam/workspaces/${workspaceId}/client-users`)
      .then((rows) => rows.filter((u) => u.role === 'client_salesman')),

  // ----- Test harness (nested under an agent) ---------------------------------
  listTestRuns: (agentId: string, page?: number, pageSize?: number) =>
    http.get<Paginated<TestHarnessRun>>(`/api/lhd/agents/${agentId}/test-runs`, clean({ page, pageSize })),
  getTestRun: (agentId: string, id: string) =>
    http.get<TestHarnessRun>(`/api/lhd/agents/${agentId}/test-runs/${id}`),
  estimateTestRun: (agentId: string, exampleCount: number) =>
    http.post<EstimateTestRunResult>(`/api/lhd/agents/${agentId}/test-runs/estimate`, { exampleCount }),
  createTestRun: (
    agentId: string,
    body: { examples: TestHarnessExample[]; confirmLargeRun?: boolean },
  ) => http.post<TestHarnessRun>(`/api/lhd/agents/${agentId}/test-runs`, body),
  /** CSV template (header row) — text/csv response. */
  testTemplateUrl: (agentId: string) => `/api/lhd/agents/${agentId}/test-runs/template`,
  /** Completed-run export — text/csv response. */
  testExportUrl: (agentId: string, id: string) =>
    `/api/lhd/agents/${agentId}/test-runs/${id}/export`,

  // ----- Triage ---------------------------------------------------------------
  listTriage: (q: TriageListQuery) =>
    http.get<Paginated<TriageItem>>('/api/lhd/triage', clean({
      page: q.page,
      pageSize: q.pageSize,
      status: q.status,
    })),
  matchTriage: (id: string, body: MatchTriageInput) =>
    http.post<TriageItem>(`/api/lhd/triage/${id}/match`, body),
  discardTriage: (id: string, reason?: string) =>
    http.post<TriageItem>(`/api/lhd/triage/${id}/discard`, { reason }),
  createConversationFromTriage: (id: string, body: MatchTriageInput) =>
    http.post<CreateConversationFromTriageResult>(`/api/lhd/triage/${id}/create-conversation`, body),

  // ----- Bookings — outcome logging -------------------------------------------
  listBookings: (q: BookingListQuery) =>
    http.get<Paginated<Booking>>('/api/lhd/bookings', clean({
      page: q.page,
      pageSize: q.pageSize,
      outcome: q.outcome,
    })),
  getBooking: (id: string) => http.get<Booking>(`/api/lhd/bookings/${id}`),
  logBookingOutcome: (id: string, body: LogBookingOutcomeInput) =>
    http.post<Booking>(`/api/lhd/bookings/${id}/outcome`, body),
};
